// The daily call list, deliberately asymmetric: a deal appears even without a date, a lead only
// once scheduled — an imported batch of 200 companies would bury the day.

import { dayDiff, stageLabel } from './salesPipeline';

export const CALL_BUCKETS = [
    { key: 'overdue',     label: 'Überfällig',  hint: 'Termin liegt in der Vergangenheit' },
    { key: 'today',       label: 'Heute',       hint: 'Heute fällig' },
    { key: 'unscheduled', label: 'Ohne Termin', hint: 'Offener Deal ohne nächsten Schritt' },
];

const BUCKET_ORDER = { overdue: 0, today: 1, unscheduled: 2 };

/** Local ISO date (yyyy-mm-dd) `days` from today — no timezone drift. */
export const isoInDays = (days, from = new Date()) => {
    const d = new Date(from);
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const bucketFor = (date, today) => {
    if (!date) return 'unscheduled';
    const days = dayDiff(date, today);
    if (days === null) return 'unscheduled';
    if (days < 0) return 'overdue';
    return days === 0 ? 'today' : null;
};

/** A deal from GET /ProjectAcquisition/follow-ups → call-list row. */
export const dealToCallItem = (dto) => ({
    kind: 'deal',
    id: dto.id,
    title: dto.project_title,
    company: dto.company_name,
    contact: dto.contact_person || '',
    phone: dto.contact_phone || '',
    email: dto.contact_email || '',
    bucket: dto.bucket,
    daysOverdue: dto.days_overdue ?? null,
    attempts: dto.contact_attempts ?? 0,
    lastContact: dto.last_contact_date || null,
    nextFollowUp: dto.next_follow_up_date || null,
    value: Number(dto.deal_value || 0),
    stage: dto.status,
    stageLabel: stageLabel(dto.status),
    priority: dto.priority || 'Medium',
    ownerName: dto.owner_name || null,
});

/** A lead → call-list row, or null when it is not due yet. */
export const leadToCallItem = (lead, today = new Date()) => {
    if (!['open', 'contacted'].includes(lead.status)) return null;
    const bucket = bucketFor(lead.nextFollowUpDate, today);
    if (bucket !== 'overdue' && bucket !== 'today') return null;
    return {
        kind: 'lead',
        id: lead.id,
        title: lead.companyName,
        company: lead.city ? `${lead.companyName} · ${lead.city}` : lead.companyName,
        contact: lead.contactPerson || '',
        phone: lead.phone || '',
        email: lead.email || '',
        bucket,
        daysOverdue: bucket === 'overdue' ? Math.abs(dayDiff(lead.nextFollowUpDate, today)) : 0,
        attempts: lead.contactAttempts ?? 0,
        lastContact: lead.lastContactDate || null,
        nextFollowUp: lead.nextFollowUpDate || null,
        value: 0,
        stage: lead.status,
        stageLabel: lead.status === 'contacted' ? 'Lead · kontaktiert' : 'Lead · neu',
        priority: lead.priority || 'Medium',
        ownerName: lead.ownerName || null,
    };
};

const PRIORITY_RANK = { High: 0, Medium: 1, Low: 2 };

/** Everything that needs a call, in working order: longest overdue, then today, then undated. */
export const buildCallList = (dealItems = [], leads = [], today = new Date()) => {
    const items = [
        ...dealItems,
        ...leads.map(lead => leadToCallItem(lead, today)).filter(Boolean),
    ];
    return items.sort((a, b) => (
        (BUCKET_ORDER[a.bucket] ?? 3) - (BUCKET_ORDER[b.bucket] ?? 3)
        || (b.daysOverdue ?? 0) - (a.daysOverdue ?? 0)
        || (PRIORITY_RANK[a.priority] ?? 1) - (PRIORITY_RANK[b.priority] ?? 1)
        || b.value - a.value
        || String(a.company).localeCompare(String(b.company), 'de')
    ));
};

export const groupByBucket = (items = []) =>
    CALL_BUCKETS
        .map(bucket => ({ ...bucket, items: items.filter(i => i.bucket === bucket.key) }))
        .filter(group => group.items.length > 0);

// Each outcome answers "when do I try again" so a logged call always leaves the
// list — that is the whole point of the follow-up loop.
export const CALL_OUTCOMES = [
    {
        key: 'not_reached',
        label: 'Nicht erreicht',
        hint: 'Wiedervorlage morgen',
        days: 1,
    },
    {
        key: 'callback',
        label: 'Später nochmal',
        hint: 'Wiedervorlage in einer Woche',
        days: 7,
    },
    {
        key: 'appointment',
        label: 'Termin vereinbart',
        hint: 'Datum wählen, Phase rückt vor',
        days: 7,
        advancesStage: true,
    },
    {
        key: 'no_interest',
        label: 'Kein Interesse',
        hint: 'Keine Wiedervorlage mehr',
        days: null,
        closes: true,
    },
];

export const outcomeByKey = (key) => CALL_OUTCOMES.find(o => o.key === key) || null;
